<script setup lang="ts">
import type { PlaceGiftTip } from '@consumer/types/serializers'
import ProfilePicture from '@consumer/components/ProfilePicture'
import { createReusableTemplate, useResizeObserver } from '@vueuse/core'
import newLightbulbIcon from '@consumer/assets/icons/new/lightbulb.svg?svgicon'

withDefaults(defineProps<{
  giftTip: PlaceGiftTip
}>(), {  })

const emit = defineEmits<{ editClicked: [PlaceGiftTip] }>()

const userName = computed(() => __props.giftTip.user?.firstName ?? __props.giftTip.user?.fullName ?? 'A User')

const [EditLinkTemplate, EditLink] = createReusableTemplate()

const giftTipRef = ref()
const showFullGiftTip = ref(false)
const showMoreButton = ref(false)

useResizeObserver(giftTipRef, () => {
  const div = giftTipRef.value
  if (div) {
    showMoreButton.value = div.offsetHeight < div.scrollHeight
  }
})
</script>

<template>
  <EditLinkTemplate>
    <div class="flex-nones">
      <EditButton v-if="giftTip.canEdit" data-testid="gift-tip-edit-button" @click="emit('editClicked', giftTip)"/>
    </div>
  </EditLinkTemplate>
  <div
    class="rounded-2xl bg-[#FFF6DE] px-6 py-4 h-full place-gift-tip"
    data-testid="place-gift-tip"
  >
    <div v-if="giftTip.user" class="flex gap-3 mb-3">
      <ProfilePicture
        :user="{name: giftTip.user.fullName}"
        :size="48"
        class="flex-none"
        :circle="false"
        bgColor="#8BC6FE"
        textColor="#225F98"
      />
      <div class="flex-grow">
        <div class="font-semibold text-lg">{{ `${userName}&apos;s Gift Tip` }}</div>
        <div class="flex items-center gap-1">
          <GIcon :svg="newLightbulbIcon" class="!stroke-none !fill-none inline-block"/>
          <span class="text-md text-v2-tertiary">Local Expert</span>
        </div>
      </div>
      <EditLink/>
    </div>
    <div v-else class="flex gap-1 items-center font-semibold mb-3">
      <GIcon :svg="newLightbulbIcon" class="!stroke-none !fill-none"/>
      <div class="flex-grow">Gift Tip</div>
      <EditLink/>
    </div>
    <div
      ref="giftTipRef"
      data-testid="gift-tip-content"
      :class="showFullGiftTip ? '' : 'limited-height'"
      v-html="giftTip.content"
    />
    <a v-if="showMoreButton" @click="showFullGiftTip = true">More</a>
  </div>
</template>
<style lang="css">
.place-gift-tip .limited-height {
  display: -webkit-box; /* note on the -webkit prefix https://developer.mozilla.org/en-US/docs/Web/CSS/line-clamp */
  /* stylelint-disable order/properties-order, order/properties-alphabetical-order */
  -webkit-box-orient: vertical;
  /* stylelint-enable order/properties-order, order/properties-alphabetical-order */
  -webkit-line-clamp: 3;
  overflow: hidden;
}
</style>
